import type { Repository } from "./repository";

// Curated starter sets of qualities, each tuned to a role. A first-run user
// picks one; the dimensions are seeded fully editable. Definitions are one line
// so a rating is calibrated against a shared sense of the quality. No mapping
// logic: canonical_key stays null everywhere (a seam for a later network phase).

export interface PackDimension {
  name: string;
  definition: string;
  // Pre-mapped to a backbone key (or null) so mapping is invisible and free for
  // pack users. Never split or blend one dimension across two keys.
  canonical_key: string | null;
  order: number;
}

export interface Pack {
  key: string;
  label: string;
  blurb: string;
  dimensions: PackDimension[];
}

// rows: [name, definition, canonical_key]. canonical_key must be a backbone key
// or null; a test asserts every pack row maps to a valid key. Domains are a
// SEPARATE, global catalogue (see domains.ts), not seeded per pack.
function pack(
  key: string,
  label: string,
  blurb: string,
  rows: [string, string, string | null][],
): Pack {
  return {
    key,
    label,
    blurb,
    dimensions: rows.map(([name, definition, canonical_key], order) => ({
      name,
      definition,
      canonical_key,
      order,
    })),
  };
}

export const GENERIC_PACK = pack(
  "generic",
  "Generic",
  "A balanced default for judging people across most professional settings.",
  [
    ["Judgement", "Quality of calls under uncertainty.", "judgement"],
    ["Integrity", "Honesty and straight dealing when it costs them.", "integrity"],
    ["Reliability", "Does what they said, on time, without chasing.", "reliability"],
    ["Insight", "Depth and honesty of the underlying analysis.", "competence"],
    ["Discretion", "Keeps confidences and is sound about what to share.", "discretion"],
  ],
);

export const MNA_PACK = pack(
  "mna",
  "M&A origination",
  "For counterparties and advisers on the deal: who closes, who is straight, who funds.",
  [
    ["Closes", "Gets deals over the line; reliable on execution and timing.", "reliability"],
    ["Integrity", "Straight dealing through the process; no surprises in conduct.", "integrity"],
    ["Sector credibility", "Knows the sector and technical substance well enough to be trusted on it.", "competence"],
    ["Financial certainty", "Funding is real and committed; low risk of finance falling away.", "financial-credibility"],
    ["Responsiveness", "Answers, turns work round, and runs a disciplined process.", "responsiveness"],
    ["Sourcing instinct", "Brings the right opportunities and reads which are worth pursuing.", "sourcing"],
    // Kept apart from deal conduct on purpose: warmth is not the same as trust.
    ["Relationship warmth", "Personal rapport and goodwill, judged separately from deal conduct.", "warmth"],
  ],
);

export const ALLOCATOR_PACK = pack(
  "allocator",
  "Allocator / manager selection",
  "For judging managers and the people who run money: rigour, discipline, conduct.",
  [
    ["Judgement", "Quality of calls under uncertainty.", "judgement"],
    ["Integrity", "Honesty and straight dealing when it costs them.", "integrity"],
    ["Operational reliability", "Does what they said, on time, without chasing.", "reliability"],
    ["Origination instinct", "Finds opportunities others miss and reads which are worth pursuing.", "sourcing"],
    ["Analytical rigour", "Depth and honesty of the underlying analysis.", "competence"],
    ["Mandate discipline", "Stays within remit and keeps confidences.", "discretion"],
  ],
);

export const CO_INVESTMENT_PACK = pack(
  "co-investment",
  "Co-investment & secondaries",
  "For co-investors and secondaries counterparts: who underwrites well and closes.",
  [
    ["Investment judgement", "Quality of investment calls under uncertainty.", "judgement"],
    ["Integrity & alignment", "Straight dealing; interests aligned with co-investors.", "integrity"],
    ["Execution reliability", "Gets deals closed and obligations met, on time.", "reliability"],
    ["Underwriting rigour", "Depth and honesty of the underwriting.", "competence"],
    ["Deal access / sourcing", "Brings real access to deals worth weighting.", "sourcing"],
    ["Funding & structuring credibility", "Capital and structuring are real and committed.", "financial-credibility"],
    ["Discretion", "Handles sensitive information appropriately.", "discretion"],
  ],
);

export const FAMILY_OFFICE_PACK = pack(
  "family-office",
  "Family office",
  "For the people around a family office: fiduciaries, advisers and connectors.",
  [
    ["Judgement", "Quality of calls under uncertainty.", "judgement"],
    ["Integrity & fiduciary conduct", "Acts as a fiduciary; straight dealing throughout.", "integrity"],
    ["Reliability", "Does what they said, on time, without chasing.", "reliability"],
    ["Domain competence", "Genuine skill in their domain.", "competence"],
    ["Access & connections", "Opens useful doors and brings worthwhile introductions.", "sourcing"],
    ["Discretion & confidentiality", "Keeps family and financial matters confidential.", "discretion"],
  ],
);

export const IC_TRUSTEE_PACK = pack(
  "ic-trustee",
  "Investment committee / trustee",
  "For fellow committee members and trustees: who prepares, contributes and keeps confidence.",
  [
    ["Judgement", "Quality of calls under uncertainty.", "judgement"],
    ["Integrity & independence", "Independent and conflicts-free; straight dealing.", "integrity"],
    ["Preparation & follow-through", "Comes prepared and follows through on actions.", "reliability"],
    ["Expertise", "Brings real expertise to the committee.", "competence"],
    ["Constructive contribution", "Shapes better decisions and moves the room constructively.", "influence"],
    ["Discretion", "Handles confidential matters appropriately.", "discretion"],
  ],
);

export const CAPITAL_RAISING_PACK = pack(
  "capital-raising",
  "Capital raising / IR / placement",
  "For placement agents, IR and fundraisers: who converts, who is straight, who has access.",
  [
    ["Conversion / does-it-close", "Turns conversations into committed capital.", "reliability"],
    ["Integrity & straight dealing", "Honest representation; no surprises.", "integrity"],
    ["Quality of access / network", "Real access to the right allocators.", "sourcing"],
    ["Commitment credibility", "Real capital and real intent behind the interest.", "financial-credibility"],
    ["Sector / strategy fluency", "Fluent in the sector and strategy being raised.", "competence"],
    ["Judgement on fit", "Reads which investors genuinely fit.", "judgement"],
    ["Discretion", "Handles sensitive fundraising information appropriately.", "discretion"],
  ],
);

export const PRIVATE_CREDIT_PACK = pack(
  "private-credit",
  "Private credit / direct lending",
  "For sponsors, borrowers and lending counterparts: credit judgement, underwriting, funding.",
  [
    ["Credit judgement", "Quality of credit calls under uncertainty.", "judgement"],
    ["Integrity & alignment", "Straight dealing; aligned through the structure.", "integrity"],
    ["Sponsor / borrower reliability", "Sponsors and borrowers do what they said.", "reliability"],
    ["Underwriting rigour", "Depth and honesty of the underwriting.", "competence"],
    ["Deal sourcing", "Brings credit opportunities worth weighting.", "sourcing"],
    ["Funding certainty", "Funding is real and committed.", "financial-credibility"],
    ["Discretion", "Handles sensitive information appropriately.", "discretion"],
  ],
);

export const PRO_SERVICES_PACK = pack(
  "pro-services",
  "Professional services / deal advisory",
  "For lawyers, accountants and advisers: advice quality, responsiveness, referrals.",
  [
    ["Judgement / advice quality", "Quality of advice and calls under uncertainty.", "judgement"],
    ["Integrity", "Straight dealing; honours commitments.", "integrity"],
    ["Responsiveness & follow-through", "Answers promptly and follows through.", "reliability"],
    ["Technical expertise", "Real technical depth in their field.", "competence"],
    ["Referral value / who they send", "Sends genuinely useful referrals.", "sourcing"],
    ["Discretion", "Handles confidential matters appropriately.", "discretion"],
  ],
);

// Career navigation: the user's OWN private read on how useful a contact is to
// their career. These are never feedback delivered to the contact; they stay
// private like every other dimension. Assemble specialises naturally ("who
// should I ask about my next step", "who can warm-intro me into X").
export const CAREER_PACK = pack(
  "career",
  "Career navigation",
  "Your private read on which contacts help your own career: doors, feedback, direction.",
  [
    ["Connector", "Opens doors and makes warm introductions.", "influence"],
    ["Honest feedback", "Gives candid, useful, straight feedback.", "integrity"],
    ["Sounding board", "Worth asking about my next steps.", "judgement"],
    ["Industry insight", "Current, useful read on the field.", "competence"],
    // Advocacy has no clean backbone analogue; subjective-only is the honest map.
    ["Advocacy", "Champions me when I am not in the room.", null],
  ],
);

// The catalogue shown in the onboarding chooser and Settings: client-facing
// roles only, no internal or firm-specific pack. Generic is NOT a card here; it
// is the "just start with the generic set" fallback.
export const PACKS: Pack[] = [
  ALLOCATOR_PACK,
  CO_INVESTMENT_PACK,
  FAMILY_OFFICE_PACK,
  IC_TRUSTEE_PACK,
  MNA_PACK,
  CAREER_PACK,
  CAPITAL_RAISING_PACK,
  PRIVATE_CREDIT_PACK,
  PRO_SERVICES_PACK,
];

// Generic is resolvable for labels and seeding even though it is not a catalogue
// card, so a generic-seeded dimension still shows a friendly purpose label.
const KNOWN_PACKS: Pack[] = [...PACKS, GENERIC_PACK];

export function packByKey(key: string): Pack | undefined {
  return KNOWN_PACKS.find((p) => p.key === key);
}

// A friendly label for a purpose key, for the cross-view purpose filter. Falls
// back to the key when it is not a known pack (e.g. a custom dimension).
export function purposeLabel(key: string): string {
  return packByKey(key)?.label ?? key;
}

// The distinct purposes present across a dimension set, with a display label and
// how many dimensions carry each, in PACKS order then any unknown keys. Drives
// the cross-view purpose filter. A dimension with no purposes counts to none.
export function purposesPresent(
  dims: { purposes: string[] }[],
): { key: string; label: string; count: number }[] {
  const count = new Map<string, number>();
  for (const d of dims) {
    for (const key of d.purposes) count.set(key, (count.get(key) ?? 0) + 1);
  }
  const known = PACKS.map((p) => p.key).filter((k) => count.has(k));
  const unknown = [...count.keys()].filter((k) => !PACKS.some((p) => p.key === k));
  return [...known, ...unknown].map((key) => ({
    key,
    label: purposeLabel(key),
    count: count.get(key) ?? 0,
  }));
}

// Past this many active dimensions, tagging overhead becomes the main usability
// risk. A SOFT cap: we warn and invite trimming, never block.
export const SOFT_DIMENSION_CAP = 10;

export interface MergedDimension {
  name: string;
  definition: string;
  canonical_key: string | null;
  purposes: string[];
  order: number;
}

// A set of distinct-named dimensions that map to the same backbone key. Kept
// separate (many bespoke labels to one canonical key is allowed and intended);
// surfaced only as a gentle "both map to X" note in the UI.
export interface SharedKeyGroup {
  canonical_key: string;
  names: string[];
}

export interface PackMerge {
  dimensions: MergedDimension[];
  sharedKeyGroups: SharedKeyGroup[];
  overSoftCap: boolean;
}

// Build the UNION of several packs' dimensions. Auto-merge ONLY exact-name
// duplicates (case-insensitive), unioning their purposes. Dimensions that share
// a canonical key but differ in name are KEPT separate and reported for a note.
export function mergePacks(packs: Pack[]): PackMerge {
  const byName = new Map<string, MergedDimension>();
  const ordered: MergedDimension[] = [];
  for (const p of packs) {
    for (const d of p.dimensions) {
      const nameKey = d.name.trim().toLowerCase();
      const existing = byName.get(nameKey);
      if (existing) {
        if (!existing.purposes.includes(p.key)) existing.purposes.push(p.key);
        continue;
      }
      const merged: MergedDimension = {
        name: d.name,
        definition: d.definition,
        canonical_key: d.canonical_key,
        purposes: [p.key],
        order: ordered.length,
      };
      byName.set(nameKey, merged);
      ordered.push(merged);
    }
  }

  const byCanon = new Map<string, string[]>();
  for (const d of ordered) {
    if (d.canonical_key === null) continue;
    const names = byCanon.get(d.canonical_key) ?? [];
    names.push(d.name);
    byCanon.set(d.canonical_key, names);
  }
  const sharedKeyGroups: SharedKeyGroup[] = [];
  for (const [canonical_key, names] of byCanon) {
    if (names.length > 1) sharedKeyGroups.push({ canonical_key, names });
  }

  return {
    dimensions: ordered,
    sharedKeyGroups,
    overSoftCap: ordered.length > SOFT_DIMENSION_CAP,
  };
}

// Seed a pack's dimensions into the store as-is (added after anything present).
// canonical_key is null: no mapping, just the persisted seam.
export async function seedPack(repo: Repository, p: Pack): Promise<void> {
  for (const d of p.dimensions) {
    await repo.addDimension(d.name, d.definition, d.canonical_key, [p.key]);
  }
}

// Seed the merged UNION of several packs (deduped, purpose-tagged). Order and
// purposes come from mergePacks. Domains are NOT seeded here: they are a global
// catalogue (domains.ts), picked per person, on a separate axis.
export async function seedPacks(repo: Repository, packs: Pack[]): Promise<void> {
  for (const d of mergePacks(packs).dimensions) {
    await repo.addDimension(d.name, d.definition, d.canonical_key, d.purposes);
  }
}

// Replace the whole dimension set with a pack. Destructive: deleting a dimension
// cascades its tags, so callers warn first when tags exist (see Settings). Used
// directly on first run, when the store is empty and there is nothing to lose.
export async function applyPack(repo: Repository, p: Pack): Promise<void> {
  await applyPacks(repo, [p]);
}

// Replace the whole dimension set with the merged union of several packs.
export async function applyPacks(repo: Repository, packs: Pack[]): Promise<void> {
  for (const d of await repo.listDimensions()) {
    await repo.deleteDimension(d.id);
  }
  await seedPacks(repo, packs);
}
